import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ProductCard, effectivePrice, inStock } from "./ProductCard";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = [
  { id: "relevancia", label: "Mais relevantes" },
  { id: "vendidos", label: "Mais vendidos" },
  { id: "menor-preco", label: "Menor preço" },
  { id: "maior-preco", label: "Maior preço" },
  { id: "lancamentos", label: "Lançamentos" },
];

const selectCls = "bg-white border border-military/20 rounded-full px-4 py-2.5 text-xs uppercase tracking-widest font-semibold text-military-dark focus:outline-none focus:border-military cursor-pointer";
const priceCls = "w-24 bg-white border border-military/20 rounded-full px-4 py-2.5 text-xs text-military-dark focus:outline-none focus:border-military";

export const ProductBrowser = ({ fixedBrand = null }) => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [category, setCategory] = useState(searchParams.get("categoria") || "Todos");
  const [brand, setBrand] = useState(searchParams.get("marca") || "Todas");
  const [onlyStock, setOnlyStock] = useState(false);
  const [onlyOffers, setOnlyOffers] = useState(searchParams.get("ofertas") === "1");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("relevancia");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    axios.get(`${API}/categories`).then((r) => setCats(r.data)).catch(() => {});
    axios.get(`${API}/brands`).then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  // menu (Categorias/Ofertas) atualiza os filtros sem recarregar
  useEffect(() => { setCategory(searchParams.get("categoria") || "Todos"); }, [searchParams]);
  useEffect(() => { setOnlyOffers(searchParams.get("ofertas") === "1"); }, [searchParams]);

  const filtered = useMemo(() => {
    if (!products) return [];
    let list = [...products];
    if (fixedBrand) list = list.filter((p) => p.brand === fixedBrand);
    else if (brand !== "Todas") list = list.filter((p) => p.brand === brand);
    if (category !== "Todos") list = list.filter((p) => p.category === category);
    if (onlyOffers) list = list.filter((p) => p.sale_price != null && p.sale_price < p.price);
    if (onlyStock) list = list.filter(inStock);
    if (minPrice !== "") list = list.filter((p) => effectivePrice(p) >= Number(minPrice));
    if (maxPrice !== "") list = list.filter((p) => effectivePrice(p) <= Number(maxPrice));
    const byOrder = (a, b) => (a.order ?? 99) - (b.order ?? 99);
    if (sort === "vendidos") list.sort((a, b) => (b.sold || 0) - (a.sold || 0) || byOrder(a, b));
    else if (sort === "menor-preco") list.sort((a, b) => effectivePrice(a) - effectivePrice(b));
    else if (sort === "maior-preco") list.sort((a, b) => effectivePrice(b) - effectivePrice(a));
    else if (sort === "lancamentos") list.sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0) || byOrder(a, b));
    else list.sort(byOrder);
    return list;
  }, [products, fixedBrand, brand, category, onlyOffers, onlyStock, minPrice, maxPrice, sort]);

  if (products === null) return <p className="text-military-dark/50 text-sm py-20 text-center">Carregando produtos...</p>;

  const usedCats = [...new Set(products.map((p) => p.category))];
  const orderedCats = cats.map((c) => c.name).filter((n) => usedCats.includes(n));
  const extraCats = usedCats.filter((n) => !orderedCats.includes(n));
  const catChips = ["Todos", ...orderedCats, ...extraCats];
  const usedBrands = [...new Set(products.map((p) => p.brand).filter(Boolean))];
  const brandOptions = brands.map((b) => b.name).filter((n) => usedBrands.includes(n));

  const clear = () => {
    setCategory("Todos"); setBrand("Todas"); setOnlyStock(false); setOnlyOffers(false); setMinPrice(""); setMaxPrice(""); setSort("relevancia");
  };
  const hasFilters = category !== "Todos" || brand !== "Todas" || onlyStock || onlyOffers || minPrice !== "" || maxPrice !== "" || sort !== "relevancia";

  return (
    <div data-testid="catalog-browser">
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="catalog-filters">
        {catChips.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setCategory(c)}
            className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-all duration-300 ${
              category === c ? "bg-military-dark text-offwhite" : "border border-military/20 text-military-dark/60 hover:border-military hover:text-military-dark"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        {!fixedBrand && brandOptions.length > 0 && (
          <select data-testid="filter-brand" translate="no" value={brand} onChange={(e) => setBrand(e.target.value)} className={selectCls + " notranslate"}>
            <option value="Todas">Todas as marcas</option>
            {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
        <div className="flex items-center gap-2">
          <input data-testid="filter-min-price" type="number" min="0" placeholder="R$ mín" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className={priceCls} />
          <span className="text-military-dark/40 text-xs">—</span>
          <input data-testid="filter-max-price" type="number" min="0" placeholder="R$ máx" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className={priceCls} />
        </div>
        <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-military-dark/60 cursor-pointer">
          <input data-testid="filter-stock" type="checkbox" checked={onlyStock} onChange={(e) => setOnlyStock(e.target.checked)} className="w-4 h-4 accent-[#4E5D46]" />
          Em estoque
        </label>
        <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-military-dark/60 cursor-pointer">
          <input data-testid="filter-offers" type="checkbox" checked={onlyOffers} onChange={(e) => setOnlyOffers(e.target.checked)} className="w-4 h-4 accent-[#4E5D46]" />
          Ofertas
        </label>
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {hasFilters && (
          <button data-testid="clear-filters" onClick={clear} className="text-xs uppercase tracking-widest font-semibold text-military-dark/50 underline underline-offset-4 hover:text-military-dark transition-colors">
            Limpar filtros
          </button>
        )}
      </div>

      <p data-testid="results-count" className="text-xs uppercase tracking-[0.2em] text-military-dark/40 mb-8">{filtered.length} produto(s)</p>

      {filtered.length === 0 ? (
        <div className="py-20 text-center" data-testid="empty-results">
          <p className="font-serif text-3xl text-military-dark mb-3">Nenhum produto encontrado.</p>
          <p className="text-sm text-military-dark/50 mb-6">Ajuste os filtros ou explore o catálogo completo.</p>
          <button onClick={clear} className="rounded-full bg-military text-offwhite px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors">Limpar filtros</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10" data-testid="product-grid">
          {filtered.map((p, i) => <ProductCard key={p.slug} p={p} i={i} />)}
        </div>
      )}
    </div>
  );
};
